import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Panel } from "./Surface";
import { StatusLabel } from "./ListTable";
import { Tags } from "./HeroDetail";
import { Icon } from "../app/Icon";
import { initials } from "../profiles/ProfileMenu";
import { PROFILE_TYPE_LABELS, type ProfileType } from "../lib/profiles";
import { getVerificationStatus, type VerificationStatus } from "../lib/verification";

const csv = (v?: string) => (v ?? "").split(",").map((s) => s.trim()).filter(Boolean);

/** Right rail for the profile editor: live preview + verification + strength. */
export function EditProfileRail({
  profileId, type, displayName, headline, fields, ownerOperator,
}: {
  profileId: string;
  type: ProfileType;
  displayName: string;
  headline: string;
  fields: Record<string, string>;
  ownerOperator: boolean;
}) {
  const { account } = useAuth();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  useEffect(() => {
    getVerificationStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  const creds = status?.credentials.filter((c) => c.profile_id === profileId) ?? [];
  const credVerified = creds.some((c) => c.status === "verified");
  const identityVerified = Boolean(account?.identity_verified);
  const equipment = csv(fields.equipment);

  const secondaryOk =
    type === "driver" ? Boolean(fields.cdl_class) :
    type === "company" ? Boolean(fields.dot_number || fields.mc_number) :
    type === "recruiter" ? Boolean(fields.agency) : Boolean(fields.authority_mc);
  const detailOk =
    type === "driver" ? equipment.length > 0 :
    type === "company" ? Boolean(fields.legal_name) :
    type === "recruiter" ? csv(fields.specialties).length > 0 : csv(fields.freight_types).length > 0;

  const checks = [
    { label: "Basic info", ok: Boolean(displayName) },
    { label: "Headline", ok: Boolean(headline) },
    { label: type === "driver" ? "CDL class" : "Numbers", ok: secondaryOk },
    { label: type === "driver" ? "Equipment" : "Details", ok: detailOk },
    { label: "Documents", ok: creds.length > 0 },
    { label: "Identity", ok: identityVerified },
  ];
  const pct = Math.round((checks.filter((c) => c.ok).length / checks.length) * 100);

  return (
    <div className="grid content-start gap-6">
      <Panel title="Profile preview">
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-lg font-black text-accent-strong">
            {initials(displayName || PROFILE_TYPE_LABELS[type])}
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-display text-base font-black">{displayName || "Your name"}</h3>
            <p className="font-mono text-[11px]">
              <span className="text-muted">{PROFILE_TYPE_LABELS[type]}</span>
              {identityVerified && credVerified && <span className="text-verified"> · VERIFIED ✓</span>}
            </p>
          </div>
        </div>
        {headline && <p className="text-sm text-muted">{headline}</p>}
        {type === "driver" && ownerOperator && <p className="font-mono text-xs text-muted">Owner-operator</p>}
        {equipment.length > 0 && <Tags items={equipment} />}
      </Panel>

      <Panel title="Verification status">
        <div className="grid gap-2.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><Icon name={identityVerified ? "verify" : "verify"} className={`h-4 w-4 ${identityVerified ? "text-verified" : "text-info"}`} /> Identity</span>
            <StatusLabel status={identityVerified ? "verified" : "pending"} />
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><Icon name="applications" className={`h-4 w-4 ${credVerified ? "text-verified" : "text-info"}`} /> Credentials</span>
            <StatusLabel status={credVerified ? "verified" : creds.length ? "in review" : "not submitted"} />
          </div>
        </div>
        <Link to="/verify" className="text-sm font-bold text-accent-strong">View details →</Link>
      </Panel>

      <Panel title="Profile strength">
        <p className="text-sm text-muted">{pct === 100 ? "Great job! Your profile is complete." : `Your profile is ${pct}% complete.`}</p>
        <div className="h-2 overflow-hidden rounded-full bg-surface-2">
          <div className="h-2 rounded-full bg-verified" style={{ width: `${pct}%` }} />
        </div>
        <ul className="grid grid-cols-2 gap-1.5 font-mono text-xs">
          {checks.map((c) => (
            <li key={c.label} className={`flex items-center gap-1.5 ${c.ok ? "text-verified" : "text-muted"}`}>
              <span>{c.ok ? "✓" : "○"}</span> {c.label}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
